import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../../../../components/Icon';
import moreVerticalIcon from '../../../../../../assets/icons/more-vertical.svg';
import { MenuButton } from '../../styles';

interface TableActionsMenuProps {
  children: React.ReactNode;
  position?: 'left-top' | 'left-bottom';
}

const TableActionsMenu: React.FC<TableActionsMenuProps> = ({ 
  children, 
  position = 'left-top' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 150;
      const menuHeight = 80;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth < 768;
      
      let top, left;
      
      if (isMobile) {
        // Mobile: menu below button, aligned to right edge
        top = rect.bottom + 8;
        left = rect.right - menuWidth;
      } else {
        // Desktop: menu to the left of button
        if (position === 'left-bottom') {
          top = rect.bottom + 5;
          left = rect.left - menuWidth - 5;
        } else {
          top = rect.top - 5;
          left = rect.left - menuWidth - 5;
        }
      }
      
      // Universal boundary checks for both mobile and desktop
      if (left < 10) {
        left = 10;
      }
      if (left + menuWidth > viewportWidth - 10) {
        left = viewportWidth - menuWidth - 10;
      }
      
      // Vertical positioning adjustments
      if (top + menuHeight > viewportHeight - 20) {
        // Not enough space below, position above the button
        top = rect.top - menuHeight - 8;
        if (top < 10) {
          // Still not enough space, position at top of viewport
          top = 10;
        }
      }
      
      if (top < 10) {
        // Not enough space above, position below the button
        top = rect.bottom + 8;
        if (top + menuHeight > viewportHeight - 20) {
          // Still not enough space, center in viewport
          top = Math.max(10, (viewportHeight - menuHeight) / 2);
        }
      }
      
      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleClose, true);
      window.addEventListener('resize', handleClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [isOpen]);

  const MenuPortal = () => {
    if (!isOpen) return null;

    const isMobile = window.innerWidth < 768;

    return createPortal(
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          zIndex: 999999,
          backgroundColor: 'white',
          border: '1px solid #e0e4ea',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: isMobile ? '140px' : '150px',
          padding: '4px 0',
          maxHeight: '200px',
          overflow: 'auto',
          fontSize: isMobile ? '14px' : '13px'
        }}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, {
              onClick: (e: React.MouseEvent) => {
                if (child.props.onClick) {
                  child.props.onClick(e);
                }
                setIsOpen(false);
              }
            });
          }
          return child;
        })}
      </div>,
      document.body
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Icon icon={moreVerticalIcon} size="20px" color="#666" />
      </button>
      <MenuPortal />
    </>
  );
};

export default TableActionsMenu;